import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../Common/Configurations/AuthContext';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import Header from '../../Common/Layout/Header';
import destinationData from '../../Data/PackageDestinations.json';
import currencyFlags from '../../Data/CurrencyFlags.json';
import './MaterPackageCreate.css'; // Importing identical styles

// Default image for package
const DEFAULT_IMAGE = "/assets/images/default-package.jpg";

// Helper to generate RFC4122-compliant GUIDs
const generateGuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Toast Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`pkg-toast ${type === 'success' ? 'pkg-toast-success' : 'pkg-toast-error'}`}>
      <div className="pkg-toast-content">
        <span>{type === 'success' ? '✅' : '❌'}</span>
        <span className="pkg-toast-message">{message}</span>
        <button onClick={onClose} className="pkg-toast-close" type="button" aria-label="Close">×</button>
      </div>
    </div>
  );
};

// Flight Sub-component
const FlightSection = ({ flight, index, onUpdate, onRemove }) => {
  const updateFlight = (field, value) => {
    onUpdate(index, { ...flight, [field]: value });
  };

  const updateSegment = (segIndex, field, value) => {
    const segments = [...(flight.Segments || [])];
    if (!segments[segIndex]) segments[segIndex] = {};
    segments[segIndex] = { ...segments[segIndex], [field]: value };
    updateFlight('Segments', segments);
  };

  const addSegment = () => {
    const segments = [...(flight.Segments || [])];
    segments.push({ From: '', To: '', Airline: '', FlightNo: '', DepartureTime: '', ArrivalTime: '' });
    updateFlight('Segments', segments);
  };

  const removeSegment = (segIndex) => {
    const segments = [...(flight.Segments || [])];
    segments.splice(segIndex, 1);
    updateFlight('Segments', segments);
  };

  return (
    <div className="pkg-subcard pkg-subcard-flight">
      <div className="pkg-subcard-header">
        <strong>✈️ Flight {index + 1}</strong>
        <button className="pkg-btn-remove" onClick={() => onRemove(index)} type="button">Remove</button>
      </div>
      <div className="pkg-grid-3">
        <div className="pkg-form-group">
          <label className="pkg-label">Airline</label>
          <input className="pkg-input" value={flight.Airline || ''} onChange={(e) => updateFlight('Airline', e.target.value)} placeholder="Airline" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Flight Number</label>
          <input className="pkg-input" value={flight.FlightNumber || ''} onChange={(e) => updateFlight('FlightNumber', e.target.value)} placeholder="Flight No" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Cabin Class</label>
          <select className="pkg-select" value={flight.CabinClass || ''} onChange={(e) => updateFlight('CabinClass', e.target.value)}>
            <option value="">Select</option>
            <option value="Economy">Economy</option>
            <option value="Premium Economy">Premium Economy</option>
            <option value="Business">Business</option>
            <option value="First">First</option>
          </select>
        </div>
      </div>
      <div className="pkg-grid-3">
        <div className="pkg-form-group">
          <label className="pkg-label">Departure City</label>
          <input className="pkg-input" value={flight.DepartureCity || ''} onChange={(e) => updateFlight('DepartureCity', e.target.value)} placeholder="From" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Arrival City</label>
          <input className="pkg-input" value={flight.ArrivalCity || ''} onChange={(e) => updateFlight('ArrivalCity', e.target.value)} placeholder="To" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Journey Type</label>
          <select className="pkg-select" value={flight.JourneyType || ''} onChange={(e) => updateFlight('JourneyType', e.target.value)}>
            <option value="">Select</option>
            <option value="One Way">One Way</option>
            <option value="Round Trip">Round Trip</option>
            <option value="Multi City">Multi City</option>
          </select>
        </div>
      </div>
      <div className="pkg-grid-3">
        <div className="pkg-form-group">
          <label className="pkg-label">Departure Date/Time</label>
          <input type="datetime-local" className="pkg-input" value={flight.DepartureDateTime || ''} onChange={(e) => updateFlight('DepartureDateTime', e.target.value)} />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Arrival Date/Time</label>
          <input type="datetime-local" className="pkg-input" value={flight.ArrivalDateTime || ''} onChange={(e) => updateFlight('ArrivalDateTime', e.target.value)} />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Baggage</label>
          <input className="pkg-input" value={flight.Baggage || ''} onChange={(e) => updateFlight('Baggage', e.target.value)} placeholder="e.g., 30kg" />
        </div>
      </div>
      <div className="pkg-grid-3">
        <div className="pkg-form-group">
          <label className="pkg-label">Adults</label>
          <input type="number" className="pkg-input" value={flight.Adults || 0} onChange={(e) => updateFlight('Adults', parseInt(e.target.value) || 0)} min="0" placeholder="0" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Kids</label>
          <input type="number" className="pkg-input" value={flight.Kids || 0} onChange={(e) => updateFlight('Kids', parseInt(e.target.value) || 0)} min="0" placeholder="0" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Infants</label>
          <input type="number" className="pkg-input" value={flight.Infants || 0} onChange={(e) => updateFlight('Infants', parseInt(e.target.value) || 0)} min="0" placeholder="0" />
        </div>
      </div>
      <div className="pkg-grid-3">
        <div className="pkg-form-group">
          <label className="pkg-label">Sale Price</label>
          <input type="number" className="pkg-input" value={flight.Price || 0} onChange={(e) => updateFlight('Price', parseFloat(e.target.value) || 0)} min="0" step="0.01" placeholder="0.00" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Option ID</label>
          <input className="pkg-input" value={flight.OptionId || ''} onChange={(e) => updateFlight('OptionId', e.target.value)} placeholder="Option" />
        </div>
      </div>
      <div className="pkg-checkbox-row">
        <input type="checkbox" id={`flight-included-${index}`} checked={flight.IsIncluded !== undefined ? flight.IsIncluded : true} onChange={(e) => updateFlight('IsIncluded', e.target.checked)} />
        <label htmlFor={`flight-included-${index}`}>Included in package</label>
      </div>

      <div style={{ marginTop: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label className="pkg-label" style={{ margin: 0 }}>Segments</label>
          <button className="pkg-btn pkg-btn-xs pkg-btn-secondary" onClick={addSegment} type="button">+ Add Segment</button>
        </div>
        {(flight.Segments || []).map((seg, segIdx) => (
          <div key={segIdx} className="pkg-subcard-nested">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <strong style={{ fontSize: '12px' }}>Segment {segIdx + 1}</strong>
              <button className="pkg-btn-remove" style={{ padding: '2px 8px' }} onClick={() => removeSegment(segIdx)} type="button">×</button>
            </div>
            <div className="pkg-grid-3">
              <input className="pkg-input" value={seg.From || ''} onChange={(e) => updateSegment(segIdx, 'From', e.target.value)} placeholder="From" />
              <input className="pkg-input" value={seg.To || ''} onChange={(e) => updateSegment(segIdx, 'To', e.target.value)} placeholder="To" />
              <input className="pkg-input" value={seg.Airline || ''} onChange={(e) => updateSegment(segIdx, 'Airline', e.target.value)} placeholder="Airline" />
            </div>
            <div className="pkg-grid-3" style={{ marginTop: '8px' }}>
              <input className="pkg-input" value={seg.FlightNo || ''} onChange={(e) => updateSegment(segIdx, 'FlightNo', e.target.value)} placeholder="Flight No" />
              <input type="datetime-local" className="pkg-input" value={seg.DepartureTime || ''} onChange={(e) => updateSegment(segIdx, 'DepartureTime', e.target.value)} />
              <input type="datetime-local" className="pkg-input" value={seg.ArrivalTime || ''} onChange={(e) => updateSegment(segIdx, 'ArrivalTime', e.target.value)} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Hotel Sub-component
const HotelSection = ({ hotel, index, onUpdate, onRemove }) => {
  const updateHotel = (field, value) => {
    onUpdate(index, { ...hotel, [field]: value });
  };

  return (
    <div className="pkg-subcard pkg-subcard-hotel">
      <div className="pkg-subcard-header">
        <strong>🏨 Hotel {index + 1}</strong>
        <button className="pkg-btn-remove" onClick={() => onRemove(index)} type="button">Remove</button>
      </div>
      <div className="pkg-grid-3">
        <div className="pkg-form-group">
          <label className="pkg-label">Hotel Name</label>
          <input className="pkg-input" value={hotel.HotelName || ''} onChange={(e) => updateHotel('HotelName', e.target.value)} placeholder="Hotel" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">City</label>
          <input className="pkg-input" value={hotel.City || ''} onChange={(e) => updateHotel('City', e.target.value)} placeholder="City" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Star Rating</label>
          <select className="pkg-select" value={hotel.StarRating || 0} onChange={(e) => updateHotel('StarRating', parseInt(e.target.value) || 0)}>
            <option value="0">Select</option>
            <option value="1">⭐</option>
            <option value="2">⭐⭐</option>
            <option value="3">⭐⭐⭐</option>
            <option value="4">⭐⭐⭐⭐</option>
            <option value="5">⭐⭐⭐⭐⭐</option>
          </select>
        </div>
      </div>
      <div className="pkg-grid-3">
        <div className="pkg-form-group">
          <label className="pkg-label">Room Type</label>
          <input className="pkg-input" value={hotel.RoomType || ''} onChange={(e) => updateHotel('RoomType', e.target.value)} placeholder="Room" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Meal Plan</label>
          <select className="pkg-select" value={hotel.MealPlan || ''} onChange={(e) => updateHotel('MealPlan', e.target.value)}>
            <option value="">Select</option>
            <option value="Room Only">Room Only</option>
            <option value="Breakfast">Breakfast</option>
            <option value="Half Board">Half Board</option>
            <option value="Full Board">Full Board</option>
            <option value="All Inclusive">All Inclusive</option>
          </select>
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Rooms</label>
          <input type="number" className="pkg-input" value={hotel.NumberOfRooms || 0} onChange={(e) => updateHotel('NumberOfRooms', parseInt(e.target.value) || 0)} min="1" placeholder="0" />
        </div>
      </div>
      <div className="pkg-grid-3">
        <div className="pkg-form-group">
          <label className="pkg-label">Nights</label>
          <input type="number" className="pkg-input" value={hotel.Nights || 0} onChange={(e) => updateHotel('Nights', parseInt(e.target.value) || 0)} min="0" placeholder="0" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Check-in</label>
          <input type="date" className="pkg-input" value={hotel.CheckInDate || ''} onChange={(e) => updateHotel('CheckInDate', e.target.value)} />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Check-out</label>
          <input type="date" className="pkg-input" value={hotel.CheckOutDate || ''} onChange={(e) => updateHotel('CheckOutDate', e.target.value)} />
        </div>
      </div>
      <div className="pkg-grid-3">
        <div className="pkg-form-group">
          <label className="pkg-label">Sale Price</label>
          <input type="number" className="pkg-input" value={hotel.Price || 0} onChange={(e) => updateHotel('Price', parseFloat(e.target.value) || 0)} min="0" step="0.01" placeholder="0.00" />
        </div>
      </div>
      <div className="pkg-checkbox-row">
        <input type="checkbox" id={`hotel-included-${index}`} checked={hotel.IsIncluded !== undefined ? hotel.IsIncluded : true} onChange={(e) => updateHotel('IsIncluded', e.target.checked)} />
        <label htmlFor={`hotel-included-${index}`}>Included in package</label>
      </div>
    </div>
  );
};

// Sightseeing Sub-component
const SightseeingSection = ({ sightseeing, index, onUpdate, onRemove }) => {
  const updateSightseeing = (field, value) => {
    onUpdate(index, { ...sightseeing, [field]: value });
  };

  return (
    <div className="pkg-subcard pkg-subcard-sightseeing">
      <div className="pkg-subcard-header">
        <strong>🗺️ Sightseeing {index + 1}</strong>
        <button className="pkg-btn-remove" onClick={() => onRemove(index)} type="button">Remove</button>
      </div>
      <div className="pkg-grid-3">
        <div className="pkg-form-group">
          <label className="pkg-label">Sightseeing Name</label>
          <input className="pkg-input" value={sightseeing.SightseeingName || ''} onChange={(e) => updateSightseeing('SightseeingName', e.target.value)} placeholder="Name" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Destination</label>
          <input className="pkg-input" value={sightseeing.Destination || ''} onChange={(e) => updateSightseeing('Destination', e.target.value)} placeholder="City" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Type</label>
          <select className="pkg-select" value={sightseeing.Type || ''} onChange={(e) => updateSightseeing('Type', e.target.value)}>
            <option value="">Select</option>
            <option value="Tour">Tour</option>
            <option value="Activity">Activity</option>
            <option value="Adventure">Adventure</option>
            <option value="Cultural">Cultural</option>
            <option value="Entertainment">Entertainment</option>
            <option value="Natural">Natural</option>
          </select>
        </div>
      </div>
      <div className="pkg-grid-3">
        <div className="pkg-form-group">
          <label className="pkg-label">Description</label>
          <input className="pkg-input" value={sightseeing.Description || ''} onChange={(e) => updateSightseeing('Description', e.target.value)} placeholder="Brief" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Duration</label>
          <input className="pkg-input" value={sightseeing.Duration || ''} onChange={(e) => updateSightseeing('Duration', e.target.value)} placeholder="e.g., 2hrs" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Date</label>
          <input type="date" className="pkg-input" value={sightseeing.Date || ''} onChange={(e) => updateSightseeing('Date', e.target.value)} />
        </div>
      </div>
      <div className="pkg-grid-3">
        <div className="pkg-form-group">
          <label className="pkg-label">Sale Price</label>
          <input type="number" className="pkg-input" value={sightseeing.Price || 0} onChange={(e) => updateSightseeing('Price', parseFloat(e.target.value) || 0)} min="0" step="0.01" placeholder="0.00" />
        </div>
      </div>
      <div className="pkg-checkbox-row">
        <input type="checkbox" id={`sight-included-${index}`} checked={sightseeing.IsIncluded !== undefined ? sightseeing.IsIncluded : true} onChange={(e) => updateSightseeing('IsIncluded', e.target.checked)} />
        <label htmlFor={`sight-included-${index}`}>Included in package</label>
      </div>
    </div>
  );
};

// Transfer Sub-component
const TransferSection = ({ transfer, index, onUpdate, onRemove }) => {
  const updateTransfer = (field, value) => {
    onUpdate(index, { ...transfer, [field]: value });
  };

  return (
    <div className="pkg-subcard pkg-subcard-transfer">
      <div className="pkg-subcard-header">
        <strong>🚗 Transfer {index + 1}</strong>
        <button className="pkg-btn-remove" onClick={() => onRemove(index)} type="button">Remove</button>
      </div>
      <div className="pkg-grid-3">
        <div className="pkg-form-group">
          <label className="pkg-label">From</label>
          <input className="pkg-input" value={transfer.FromLocation || ''} onChange={(e) => updateTransfer('FromLocation', e.target.value)} placeholder="Pickup" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">To</label>
          <input className="pkg-input" value={transfer.ToLocation || ''} onChange={(e) => updateTransfer('ToLocation', e.target.value)} placeholder="Dropoff" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Type</label>
          <select className="pkg-select" value={transfer.Type || ''} onChange={(e) => updateTransfer('Type', e.target.value)}>
            <option value="">Select</option>
            <option value="Airport Transfer">Airport Transfer</option>
            <option value="Intercity">Intercity</option>
            <option value="Sightseeing">Sightseeing</option>
            <option value="Private">Private</option>
            <option value="Shared">Shared</option>
          </select>
        </div>
      </div>
      <div className="pkg-grid-3">
        <div className="pkg-form-group">
          <label className="pkg-label">Vehicle</label>
          <input className="pkg-input" value={transfer.VehicleDetails || ''} onChange={(e) => updateTransfer('VehicleDetails', e.target.value)} placeholder="Sedan/SUV" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Pickup Time</label>
          <input type="time" className="pkg-input" value={transfer.PickupTime || ''} onChange={(e) => updateTransfer('PickupTime', e.target.value)} />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Date</label>
          <input type="date" className="pkg-input" value={transfer.Date || ''} onChange={(e) => updateTransfer('Date', e.target.value)} />
        </div>
      </div>
      <div className="pkg-grid-3">
        <div className="pkg-form-group">
          <label className="pkg-label">Sale Price</label>
          <input type="number" className="pkg-input" value={transfer.Price || 0} onChange={(e) => updateTransfer('Price', parseFloat(e.target.value) || 0)} min="0" step="0.01" placeholder="0.00" />
        </div>
      </div>
      <div className="pkg-checkbox-row">
        <input type="checkbox" id={`transfer-included-${index}`} checked={transfer.IsIncluded !== undefined ? transfer.IsIncluded : true} onChange={(e) => updateTransfer('IsIncluded', e.target.checked)} />
        <label htmlFor={`transfer-included-${index}`}>Included in package</label>
      </div>
    </div>
  );
};

const STEPS = [
  { id: 1, label: 'Basic Info' },
  { id: 2, label: 'Destinations & Travelers' },
  { id: 3, label: 'Dates & Pricing' },
  { id: 4, label: 'Settings & Contact' },
  { id: 5, label: 'Itinerary' },
  { id: 6, label: 'Inclusions & Terms' },
];

// Main Component
const CompanyPackageEdit = () => {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [toast, setToast] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [showEstimation, setShowEstimation] = useState(false);

  // DMC Selection States
  const [selectedDmc, setSelectedDmc] = useState(null);
  const [dmcList, setDmcList] = useState([]);
  const [loadingDmcList, setLoadingDmcList] = useState(false);

  // Price Sharing state
  const [priceSharingItems, setPriceSharingItems] = useState([]);

  // Destination suggestion state
  const [destinationInput, setDestinationInput] = useState('');
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [selectedPlaces, setSelectedPlaces] = useState([]);
  const [showPlaceDropdown, setShowPlaceDropdown] = useState(false);
  const [currentDestination, setCurrentDestination] = useState(null);

  // Currency options from currencyFlags
  const [currencyOptions, setCurrencyOptions] = useState([]);

  // Mapping configurations
  const statusMap = {
    'draft': 0,
    'active': 1,
    'inactive': 2,
    'published': 3,
    'archived': 4
  };

  const statusMapReverse = {
    0: 'draft',
    1: 'active',
    2: 'inactive',
    3: 'published',
    4: 'archived'
  };

  // Process currency data from currencyFlags.json
  useEffect(() => {
    if (currencyFlags && Array.isArray(currencyFlags)) {
      const sortedCountries = [...currencyFlags].sort((a, b) =>
        a.country.localeCompare(b.country)
      );

      const uniqueCurrencies = [];
      const currencyMap = new Map();

      sortedCountries.forEach((country) => {
        if (country.code && !currencyMap.has(country.code)) {
          currencyMap.set(country.code, true);
          uniqueCurrencies.push({
            code: country.code,
            symbol: country.symbol || country.code,
            name: country.currency || country.country || '',
            flag: country.flag || '',
            country: country.country || '',
          });
        }
      });

      uniqueCurrencies.sort((a, b) => a.code.localeCompare(b.code));
      setCurrencyOptions(uniqueCurrencies);
    }
  }, []);

  // Fetch DMCs for the dropdown
  const fetchDMCs = useCallback(async () => {
    if (!user?.cid) return;
    
    setLoadingDmcList(true);
    try {
      const apiUrl = `${config.GETCOMPANIES}?companyType=DMC&PageNumber=1&PageSize=100&parentCompanyId=${user.cid}`;
      const response = await apiService.getWithParams(apiUrl);
      
      if (response.data && Array.isArray(response.data)) {
        setDmcList(response.data);
      } else {
        setDmcList([]);
      }
    } catch (error) {
      console.error('Error fetching DMCs:', error);
      setDmcList([]);
    } finally {
      setLoadingDmcList(false);
    }
  }, [user]);

  // Call this when component mounts
  useEffect(() => {
    if (user) {
      fetchDMCs();
    }
  }, [user, fetchDMCs]);

  // Handle DMC selection
  const handleDmcSelect = (dmc) => {
    setSelectedDmc(dmc);
    
    if (dmc) {
      // Auto-fill contact information from selected DMC
      setFormData(prev => ({
        ...prev,
        ContactName: dmc.brandName || dmc.companyName || '',
        ContactEmail: dmc.email || '',
        ContactPhoneNumber: dmc.whatsAppNumber || '',
        CompanyId: dmc.companyId,
        CompanyName: dmc.companyName || '',
        CreatedBy: dmc.brandName || dmc.companyName || '',
        Source: 'Dmc'
      }));
    } else {
      // Reset to user's contact info when no DMC selected
      setFormData(prev => ({
        ...prev,
        ContactName: user?.brandName || '',
        ContactEmail: user?.companyEmail || '',
        ContactPhoneNumber: user?.companyWhatsappnumber || '',
        CompanyId: user?.cid || '',
        CompanyName: user?.companyname || '',
        CreatedBy: user?.name || user?.email || '',
        Source: 'Manual'
      }));
    }
  };

  const inclusionSubTabs = [
    { id: 'inclusions', label: '✅ Inclusions' },
    { id: 'exclusions', label: '❌ Exclusions' },
    { id: 'terms', label: '📄 Terms' },
  ];

  const [inclusionActiveSubTab, setInclusionActiveSubTab] = useState('inclusions');

  // Merged: Combined both travelTypes and holidayTypes options here
  const travelTypes = [
    'Family',
    'Group',
    'Solo',
    'Honeymoon',
    'Leisure',
    'Business',
    'Adventure',
    'Pilgrimage',
    'Pilgrimage and Leisure Combo',
    'Luxury',
    'Budget',
  ];
  const statusOptions = ['draft', 'active', 'inactive', 'published', 'archived'];

  // Price Sharing Handlers
  const handleAddPriceSharing = () => {
    const newItem = {
      Price: 0,
      SharingType: 'Double',
      AdultPrice: 0,
      KidPrice: 0,
      InfantPrice: 0
    };
    setPriceSharingItems([...priceSharingItems, newItem]);
  };

  const handleUpdatePriceSharing = (index, field, value) => {
    const updatedItems = [...priceSharingItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setPriceSharingItems(updatedItems);
  };

  const handleRemovePriceSharing = (index) => {
    setPriceSharingItems(priceSharingItems.filter((_, i) => i !== index));
  };

  // Destination suggestion handler
  const handleDestinationInputChange = (e) => {
    const value = e.target.value;
    setDestinationInput(value);

    if (value.trim().length > 0) {
      const filtered = destinationData.filter(dest =>
        dest.Destination.toLowerCase().includes(value.toLowerCase())
      );
      setDestinationSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setDestinationSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectDestination = (destination) => {
    const newPlaces = destination.Places.map(place => ({
      place: place,
      destination: destination.Destination
    }));

    const uniqueNewPlaces = newPlaces.filter(
      p => !selectedPlaces.some(existing => existing.place === p.place)
    );

    if (uniqueNewPlaces.length > 0) {

      const updatedPlaces = [...selectedPlaces, ...uniqueNewPlaces];
      setSelectedPlaces(updatedPlaces);

      const placeNames = updatedPlaces.map(p => p.place).join(', ');
      setFormData(prev => ({
        ...prev,
        DestinationsCover: placeNames
      }));

      if (!selectedDestinations.find(d => d.Destination === destination.Destination)) {
        setSelectedDestinations([...selectedDestinations, destination]);
      }

      setCurrentDestination(destination);
      setShowPlaceDropdown(true);
    }

    setDestinationInput('');
    setDestinationSuggestions([]);
    setShowSuggestions(false);
  };

  const handleRemoveSelectedDestination = (destinationToRemove) => {
    const updated = selectedDestinations.filter(d => d.Destination !== destinationToRemove.Destination);
    setSelectedDestinations(updated);

    const updatedPlaces = selectedPlaces.filter(p => p.destination !== destinationToRemove.Destination);
    setSelectedPlaces(updatedPlaces);

    const placeNames = updatedPlaces.map(p => p.place).join(', ');
    setFormData(prev => ({
      ...prev,
      DestinationsCover: placeNames
    }));

    if (currentDestination?.Destination === destinationToRemove.Destination) {
      setCurrentDestination(null);
      setShowPlaceDropdown(false);
    }
  };
  void handleRemoveSelectedDestination;

  // Place selection handlers
  const handleTogglePlace = (place) => {
    const placeObj = {
      place: place,
      destination: currentDestination.Destination
    };

    let updatedPlaces;
    const exists = selectedPlaces.some(p => p.place === place && p.destination === currentDestination.Destination);

    if (exists) {
      updatedPlaces = selectedPlaces.filter(p => !(p.place === place && p.destination === currentDestination.Destination));
    } else {
      updatedPlaces = [...selectedPlaces, placeObj];
    }

    setSelectedPlaces(updatedPlaces);

    const placeNames = updatedPlaces.map(p => p.place).join(', ');
    setFormData(prev => ({
      ...prev,
      DestinationsCover: placeNames
    }));
  };

  const handleRemoveSelectedPlace = (placeToRemove) => {
    const updatedPlaces = selectedPlaces.filter(p => !(p.place === placeToRemove.place && p.destination === placeToRemove.destination));
    setSelectedPlaces(updatedPlaces);

    const placeNames = updatedPlaces.map(p => p.place).join(', ');
    setFormData(prev => ({
      ...prev,
      DestinationsCover: placeNames
    }));
  };

  const handleManualPlaceAdd = () => {
    const input = document.getElementById('manualPlaceInput');
    const placeName = input?.value?.trim();

    if (placeName) {
      if (!selectedPlaces.some(p => p.place === placeName)) {
        const placeObj = {
          place: placeName,
          destination: 'Manual Entry'
        };

        const updatedPlaces = [...selectedPlaces, placeObj];
        setSelectedPlaces(updatedPlaces);

        const placeNames = updatedPlaces.map(p => p.place).join(', ');
        setFormData(prev => ({
          ...prev,
          DestinationsCover: placeNames
        }));

        if (!selectedDestinations.find(d => d.Destination === 'Manual Entry')) {
          setSelectedDestinations([...selectedDestinations, { Destination: 'Manual Entry', Places: [placeName] }]);
        }
      }

      input.value = '';
    }
  };

  const isPlaceSelected = (place) => {
    return selectedPlaces.some(p => p.place === place && p.destination === currentDestination?.Destination);
  };

  // Initialize formData
  const [formData, setFormData] = useState({
    Title: '',
    Description: '',
    TravelType: '',
    HolidayType: '',
    HolidayKey: '',
    Duration: '',
    TotalNights: 0,
    TotalDays: 0,
    DestinationsCover: '',
    StartedDate: '',
    ValidityFrom: '',
    ValidityTo: '',
    NoOfPersons: 0,
    Adults: 0,
    Kids: 0,
    Infants: 0,
    Currency: 'INR',
    FlightPrice: 0,
    HotelPrice: 0,
    SightSeeingPrice: 0,
    TransferPrice: 0,
    OtherPrice: 0,
    PurchasePrice: 0,
    SalePrice: 0,
    ContactName: '',
    ContactPhoneNumber: '',
    ContactEmail: '',
    Inclusions: [],
    Exclusions: [],
    Terms: [],
    Itinerary: [],
    Destinations: [],
    Status: 'draft',
    ContentType: '',
    IsWebsite: false,
    IsLive: false,
    ImageUrl: '',
    ImageName: '',
    Source: 'Manual',
    CompanyId: '',
    CreatedBy: '',
    CompanyName: '',
  });

  // Update contact info when user loads
  useEffect(() => {
    if (user && !selectedDmc) {
      setFormData(prev => ({
        ...prev,
        ContactName: user?.brandName || '',
        ContactPhoneNumber: user?.companyWhatsappnumber || '',
        ContactEmail: user?.companyEmail || '',
        CompanyId: user?.cid || '',
        CompanyName: user?.companyname || '',
        CreatedBy: user?.name || user?.email || '',
      }));
    }
  }, [user, selectedDmc]);

  // Parse date for input fields
  const parseDateToInput = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  };

  const parseDateTimeToInput = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 16);
  };

  // Fetch package data for editing
  useEffect(() => {
    const fetchPackageData = async () => {
      if (!id) return;
      const safeParseArray = (arr) => {
        if (!arr || !Array.isArray(arr)) return [];
        if (arr.length === 1 && typeof arr[0] === 'string' && arr[0].trim().startsWith('[')) {
          try {
            const parsed = JSON.parse(arr[0]);
            if (Array.isArray(parsed)) return parsed;
          } catch (e) {
            console.error("Error parsing list:", e);
          }
        }
        return arr;
      };

      try {
        setIsFetching(true);
        const res = await apiService.get(`${config.GETCOMPANYPACKAGEBYID}/${id}`);
        console.log("Package data:", res);
        
        if (res) {
          const mappedData = {
            Title: res.title || '',
            Description: res.description || '',
            TravelType: res.travelType || '',
            HolidayType: res.holidayType || '',
            HolidayKey: res.holidayKey || '',
            Duration: res.duration || '',
            TotalNights: res.totalNights || 0,
            TotalDays: res.totalDays || 0,
            DestinationsCover: res.destinationsCover || '',
            StartedDate: parseDateToInput(res.startedDate),
            ValidityFrom: parseDateToInput(res.validity?.from),
            ValidityTo: parseDateToInput(res.validity?.to),
            NoOfPersons: res.noOfPersons || 0,
            Adults: res.adults || 0,
            Kids: res.kids || 0,
            Infants: res.infants || 0,
            Currency: res.currency || 'INR',
            FlightPrice: res.flightPrice || 0,
            HotelPrice: res.hotelPrice || 0,
            SightSeeingPrice: res.sightSeeingPrice || 0,
            TransferPrice: res.transferPrice || 0,
            OtherPrice: res.otherPrice || 0,
            PurchasePrice: res.purchasePrice || 0,
            SalePrice: res.salePrice || 0,
            ContactName: res.contactName || user?.name || '',
            ContactPhoneNumber: res.contactPhoneNumber || user?.phoneNumber || '',
            ContactEmail: res.contactEmail || user?.email || '',
            Inclusions: safeParseArray(res.inclusions),
            Exclusions: safeParseArray(res.exclusions),
            Terms: (safeParseArray(res.terms) || []).map(term => ({
              Title: term.Title || term.title || '',
              Description: term.Description || term.description || '',
              PackageTermsAndConditionId: term.PackageTermsAndConditionId || term.packageTermsAndConditionId || generateGuid()
            })),
            Itinerary: (res.itinerary || []).map(day => ({
              Day: day.day || 0,
              Title: day.title || '',
              Description: day.description || '',
              Location: day.location || '',
              Meals: day.meals || '',
              Note: day.note || '',
              Date: parseDateToInput(day.date),
              Flights: (day.flights || []).map(f => ({
                PackageFlightBookingId: f.packageFlightBookingId || generateGuid(),
                Airline: f.airline || '',
                FlightNumber: f.flightNumber || '',
                DepartureCity: f.departureCity || '',
                ArrivalCity: f.arrivalCity || '',
                DepartureDateTime: parseDateTimeToInput(f.departureDateTime),
                ArrivalDateTime: parseDateTimeToInput(f.arrivalDateTime),
                CabinClass: f.cabinClass || '',
                JourneyType: f.journeyType || '',
                Adults: f.adults || 0,
                Kids: f.kids || 0,
                Infants: f.infants || 0,
                Baggage: f.baggage || '',
                OptionId: f.optionId || '',
                IsIncluded: f.isIncluded !== undefined ? f.isIncluded : true,
                Price: f.price || 0,
                Segments: (f.segments || []).map(s => ({
                  From: s.from || '',
                  To: s.to || '',
                  Airline: s.airline || '',
                  FlightNo: s.flightNo || '',
                  DepartureTime: parseDateTimeToInput(s.departureTime),
                  ArrivalTime: parseDateTimeToInput(s.arrivalTime)
                }))
              })),
              Hotels: (day.hotels || []).map(h => ({
                PackageHotelBookingId: h.packageHotelBookingId || generateGuid(),
                HotelName: h.hotelName || '',
                City: h.city || '',
                StarRating: h.starRating || 0,
                RoomType: h.roomType || '',
                MealPlan: h.mealPlan || '',
                NumberOfRooms: h.numberOfRooms || 1,
                Nights: h.nights || 0,
                CheckInDate: parseDateToInput(h.checkInDate),
                CheckOutDate: parseDateToInput(h.checkOutDate),
                IsIncluded: h.isIncluded !== undefined ? h.isIncluded : true,
                Price: h.price || 0
              })),
              Sightseeing: (day.sightseeing || []).map(s => ({
                PackageSightSeeingId: s.packageSightSeeingId || generateGuid(),
                SightseeingName: s.sightseeingName || '',
                Destination: s.destination || '',
                Description: s.description || '',
                Duration: s.duration || '',
                Type: s.type || '',
                Date: parseDateToInput(s.date),
                IsIncluded: s.isIncluded !== undefined ? s.isIncluded : true,
                Price: s.price || 0
              })),
              Transfers: (day.transfers || []).map(t => ({
                PackageTransferId: t.packageTransferId || generateGuid(),
                FromLocation: t.fromLocation || '',
                ToLocation: t.toLocation || '',
                VehicleDetails: t.vehicleDetails || '',
                Type: t.type || '',
                PickupTime: t.pickupTime || '',
                Date: parseDateToInput(t.date),
                IsIncluded: t.isIncluded !== undefined ? t.isIncluded : true,
                Price: t.price || 0
              }))
            })),
            Destinations: res.destinations || [],
            Status: statusMapReverse[res.status] !== undefined ? statusMapReverse[res.status] : 'draft',
            ContentType: res.contentType || '',
            IsWebsite: res.isWebsite || false,
            IsLive: res.isLive || false,
            ImageName: res.imageName || '',
            ImageUrl: res.imageSrc || '',
            Source: res.source || 'Manual',
            CompanyId: res.companyId || '',
            CreatedBy: res.createdBy || '',
            CompanyName: res.companyName || '',
          };

          setFormData(prev => ({ ...prev, ...mappedData }));

          // Handle DMC selection - check if package has a DMC associated
          if (res.source === 'Dmc' && res.companyId) {
            // Try to find the DMC in the loaded list
            let dmc = dmcList.find(d => d.companyId === res.companyId);
            
            if (!dmc) {
              // If not in list, try to fetch it specifically
              try {
                const dmcResponse = await apiService.get(`${config.GETCOMPANYBYID}/${res.companyId}`);
                if (dmcResponse) {
                  dmc = dmcResponse;
                  // Add to dmcList for future use
                  setDmcList(prev => {
                    const exists = prev.some(d => d.companyId === dmc.companyId);
                    if (!exists) {
                      return [...prev, dmc];
                    }
                    return prev;
                  });
                }
              } catch (err) {
                console.error('Error fetching DMC details:', err);
                // Create a placeholder DMC object from package data
                dmc = {
                  companyId: res.companyId,
                  companyName: res.companyName || 'Unknown DMC',
                  brandName: res.createdBy || '',
                  email: res.contactEmail || '',
                  phoneNumber: res.contactPhoneNumber || '',
                };
              }
            }
            
            if (dmc) {
              setSelectedDmc(dmc);
              // Set contact info from DMC
              setFormData(prev => ({
                ...prev,
                ContactName: dmc.brandName || dmc.companyName || '',
                ContactEmail: dmc.email || '',
                ContactPhoneNumber: dmc.phoneNumber || '',
                CompanyId: dmc.companyId,
                CompanyName: dmc.companyName || '',
                CreatedBy: dmc.brandName || dmc.companyName || '',
                Source: 'Dmc'
              }));
            }
          } else {
            // No DMC associated, use user's info
            setSelectedDmc(null);
          }

          // Handle image preview
          if (res.imageSrc) {
            setImagePreview(res.imageSrc);
          }

          if (res.priceSharing && Array.isArray(res.priceSharing)) {
            const mappedPriceSharing = res.priceSharing.map(item => ({
              Price: item.price || item.Price || 0,
              SharingType: item.sharingType || item.SharingType || 'Double',
              AdultPrice: item.adultPrice || item.AdultPrice || 0,
              KidPrice: item.kidPrice || item.KidPrice || 0,
              InfantPrice: item.infantPrice || item.InfantPrice || 0
            }));
            setPriceSharingItems(mappedPriceSharing);
          }

          if (res.destinationsCover) {
            // Split the comma-separated places list
            const placeNames = res.destinationsCover.split(',').map(d => d.trim()).filter(Boolean);

            // 1. Map each place name to the expected object structure: { place, destination }
            const mappedPlaces = placeNames.map(placeName => {
              // Search which destination category in destinationData contains this place name
              const foundDest = destinationData.find(d =>
                d.Places && d.Places.some(p => p.trim().toLowerCase() === placeName.toLowerCase())
              );

              return {
                place: placeName,
                destination: foundDest ? foundDest.Destination : 'Manual Entry'
              };
            });

            // 2. Set selectedPlaces so the UI pills render correctly
            setSelectedPlaces(mappedPlaces);

            // 3. Reconstruct the unique selected destinations list for panel logic
            const uniqueDestinations = [];
            mappedPlaces.forEach(p => {
              if (p.destination !== 'Manual Entry') {
                const destObj = destinationData.find(d => d.Destination === p.destination);
                if (destObj && !uniqueDestinations.some(d => d.Destination === destObj.Destination)) {
                  uniqueDestinations.push(destObj);
                }
              } else {
                if (!uniqueDestinations.some(d => d.Destination === 'Manual Entry')) {
                  uniqueDestinations.push({ Destination: 'Manual Entry', Places: [] });
                }
              }
            });

            setSelectedDestinations(uniqueDestinations);
            if (uniqueDestinations.length > 0) {
              setCurrentDestination(uniqueDestinations[0]);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching package details for edit:', error);
        setToast({ message: 'Failed to fetch package details. Please try again.', type: 'error' });
      } finally {
        setIsFetching(false);
      }
    };

    if (user && !authLoading && id) {
      fetchPackageData();
    }
  }, [id, user, authLoading, dmcList]);

  // Effect for auto-calculation of Total Days and Duration from Total Nights
  useEffect(() => {
    if (formData.TotalNights > 0) {
      const days = formData.TotalNights + 1;
      const durationLabel = `${days}D/${formData.TotalNights}N`;
      if (formData.TotalDays !== days || formData.Duration !== durationLabel) {
        setFormData(prev => ({
          ...prev,
          TotalDays: days,
          Duration: durationLabel
        }));
      }
    }
  }, [formData.TotalNights]);

  // Effect for auto-calculation of Total Persons
  useEffect(() => {
    const total = (formData.Adults || 0) + (formData.Kids || 0) + (formData.Infants || 0);
    if (formData.NoOfPersons !== total) {
      setFormData(prev => ({
        ...prev,
        NoOfPersons: total
      }));
    }
  }, [formData.Adults, formData.Kids, formData.Infants]);

  // Handle manual days/nights change
  const handleDaysNightsChange = (field, value) => {
    const numValue = parseInt(value) || 0;
    setFormData(prev => {
      const newData = { ...prev, [field]: numValue };
      const days = field === 'TotalDays' ? numValue : prev.TotalDays;
      const nights = field === 'TotalNights' ? numValue : prev.TotalNights;
      if (days > 0 || nights > 0) {
        const durationLabel = `${days}D/${nights}N`;
        newData.Duration = durationLabel;
      }
      return newData;
    });
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // Array handlers
  const handleAddArrayItem = (field, value) => {
    if (!value.trim()) return;
    setFormData(prev => ({ ...prev, [field]: [...prev[field], value.trim()] }));
  };

  const handleRemoveArrayItem = (field, index) => {
    setFormData(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
  };

  // Terms handlers
  const handleAddTerm = () => {
    const title = document.getElementById('termTitle')?.value;
    const description = document.getElementById('termDescription')?.value;
    if (title?.trim()) {
      setFormData(prev => ({
        ...prev,
        Terms: [...prev.Terms, { Title: title.trim(), Description: description?.trim() || '' }]
      }));
      document.getElementById('termTitle').value = '';
      document.getElementById('termDescription').value = '';
    }
  };

  const handleRemoveTerm = (index) => {
    setFormData(prev => ({ ...prev, Terms: prev.Terms.filter((_, i) => i !== index) }));
  };

  // Itinerary handlers
  const handleAddItineraryDay = () => {
    setFormData(prev => ({
      ...prev,
      Itinerary: [
        ...prev.Itinerary,
        {
          Day: prev.Itinerary.length + 1,
          Title: '',
          Description: '',
          Location: '',
          Meals: '',
          Note: '',
          Date: '',
          Flights: [],
          Hotels: [],
          Sightseeing: [],
          Transfers: []
        }
      ]
    }));
  };

  const handleItineraryChange = (index, field, value) => {
    setFormData(prev => {
      const newItinerary = [...prev.Itinerary];
      newItinerary[index] = { ...newItinerary[index], [field]: value };
      return { ...prev, Itinerary: newItinerary };
    });
  };

  const handleRemoveItineraryDay = (index) => {
    setFormData(prev => ({ ...prev, Itinerary: prev.Itinerary.filter((_, i) => i !== index) }));
  };

  // Flight handlers
  const handleAddFlight = (dayIndex) => {
    setFormData(prev => {
      const newItinerary = [...prev.Itinerary];
      newItinerary[dayIndex].Flights.push({
        PackageFlightBookingId: generateGuid(),
        Airline: '',
        FlightNumber: '',
        DepartureCity: '',
        ArrivalCity: '',
        DepartureDateTime: '',
        ArrivalDateTime: '',
        CabinClass: '',
        JourneyType: '',
        Adults: 0,
        Kids: 0,
        Infants: 0,
        Baggage: '',
        OptionId: '',
        IsIncluded: true,
        Price: 0,
        Segments: []
      });
      return { ...prev, Itinerary: newItinerary };
    });
  };

  const handleUpdateFlight = (dayIndex, flightIndex, updatedFlight) => {
    setFormData(prev => {
      const newItinerary = [...prev.Itinerary];
      newItinerary[dayIndex].Flights[flightIndex] = updatedFlight;
      return { ...prev, Itinerary: newItinerary };
    });
  };

  const handleRemoveFlight = (dayIndex, flightIndex) => {
    setFormData(prev => {
      const newItinerary = [...prev.Itinerary];
      newItinerary[dayIndex].Flights.splice(flightIndex, 1);
      return { ...prev, Itinerary: newItinerary };
    });
  };

  // Hotel handlers
  const handleAddHotel = (dayIndex) => {
    setFormData(prev => {
      const newItinerary = [...prev.Itinerary];
      newItinerary[dayIndex].Hotels.push({
        PackageHotelBookingId: generateGuid(),
        HotelName: '',
        City: '',
        StarRating: 0,
        RoomType: '',
        MealPlan: '',
        NumberOfRooms: 1,
        Nights: 0,
        CheckInDate: '',
        CheckOutDate: '',
        IsIncluded: true,
        Price: 0
      });
      return { ...prev, Itinerary: newItinerary };
    });
  };

  const handleUpdateHotel = (dayIndex, hotelIndex, updatedHotel) => {
    setFormData(prev => {
      const newItinerary = [...prev.Itinerary];
      newItinerary[dayIndex].Hotels[hotelIndex] = updatedHotel;
      return { ...prev, Itinerary: newItinerary };
    });
  };

  const handleRemoveHotel = (dayIndex, hotelIndex) => {
    setFormData(prev => {
      const newItinerary = [...prev.Itinerary];
      newItinerary[dayIndex].Hotels.splice(hotelIndex, 1);
      return { ...prev, Itinerary: newItinerary };
    });
  };

  // Sightseeing handlers
  const handleAddSightseeing = (dayIndex) => {
    setFormData(prev => {
      const newItinerary = [...prev.Itinerary];
      newItinerary[dayIndex].Sightseeing.push({
        PackageSightSeeingId: generateGuid(),
        SightseeingName: '',
        Destination: '',
        Description: '',
        Duration: '',
        Type: '',
        Date: '',
        IsIncluded: true,
        Price: 0
      });
      return { ...prev, Itinerary: newItinerary };
    });
  };

  const handleUpdateSightseeing = (dayIndex, ssIndex, updatedSightseeing) => {
    setFormData(prev => {
      const newItinerary = [...prev.Itinerary];
      newItinerary[dayIndex].Sightseeing[ssIndex] = updatedSightseeing;
      return { ...prev, Itinerary: newItinerary };
    });
  };

  const handleRemoveSightseeing = (dayIndex, ssIndex) => {
    setFormData(prev => {
      const newItinerary = [...prev.Itinerary];
      newItinerary[dayIndex].Sightseeing.splice(ssIndex, 1);
      return { ...prev, Itinerary: newItinerary };
    });
  };

  // Transfer handlers
  const handleAddTransfer = (dayIndex) => {
    setFormData(prev => {
      const newItinerary = [...prev.Itinerary];
      newItinerary[dayIndex].Transfers.push({
        PackageTransferId: generateGuid(),
        FromLocation: '',
        ToLocation: '',
        VehicleDetails: '',
        Type: '',
        PickupTime: '',
        Date: '',
        IsIncluded: true,
        Price: 0
      });
      return { ...prev, Itinerary: newItinerary };
    });
  };

  const handleUpdateTransfer = (dayIndex, transferIndex, updatedTransfer) => {
    setFormData(prev => {
      const newItinerary = [...prev.Itinerary];
      newItinerary[dayIndex].Transfers[transferIndex] = updatedTransfer;
      return { ...prev, Itinerary: newItinerary };
    });
  };

  const handleRemoveTransfer = (dayIndex, transferIndex) => {
    setFormData(prev => {
      const newItinerary = [...prev.Itinerary];
      newItinerary[dayIndex].Transfers.splice(transferIndex, 1);
      return { ...prev, Itinerary: newItinerary };
    });
  };

  // Validate form
  const validateForm = () => {
    const required = ['Title', 'Description', 'TotalDays', 'TotalNights', 'DestinationsCover', 'SalePrice'];
    for (const field of required) {
      if (!formData[field] || (typeof formData[field] === 'string' && !formData[field].trim())) {
        setToast({ message: `Please fill in "${field}"`, type: 'error' });
        return false;
      }
    }
    if (formData.TotalDays <= 0) {
      setToast({ message: 'Total Days must be greater than 0', type: 'error' });
      return false;
    }
    if (formData.SalePrice <= 0) {
      setToast({ message: 'Sale Price must be greater than 0', type: 'error' });
      return false;
    }
    return true;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    if (!user?.cid) {
      setToast({ message: 'Company ID not found. Please login again.', type: 'error' });
      return;
    }

    setIsLoading(true);

    try {
      // ==========================================
      // AUTO-COMPILE DESTINATIONS FROM ITINERARY
      // ==========================================
      const compiledDestinations = [];
      const cityMap = new Map();
      let sequenceNum = 1;

      (formData.Itinerary || []).forEach(day => {
        (day.Hotels || []).forEach(hotel => {
          const cityName = hotel.City || day.Location || "Unknown";
          const normalizedCity = cityName.trim().toLowerCase();

          if (!cityMap.has(normalizedCity)) {
            const placeObj = {
              CityCode: hotel.City?.substring(0, 3).toUpperCase() || "CTY",
              CityName: cityName,
              Nights: parseInt(hotel.Nights) || 1,
              Sequence: sequenceNum++,
              Hotels: [
                {
                  HotelId: hotel.PackageHotelBookingId && !hotel.PackageHotelBookingId.startsWith('new-')
                    ? hotel.PackageHotelBookingId
                    : generateGuid(),
                  HotelName: hotel.HotelName || "Suggested Hotel",
                  RoomType: hotel.RoomType || "",
                  MealPlan: hotel.MealPlan || "",
                  StarRating: parseInt(hotel.StarRating) || 0
                }
              ]
            };
            cityMap.set(normalizedCity, placeObj);
          } else {
            const existingPlace = cityMap.get(normalizedCity);
            existingPlace.Nights += (parseInt(hotel.Nights) || 0);

            const hotelExists = existingPlace.Hotels.some(h => h.HotelName === hotel.HotelName);
            if (!hotelExists) {
              existingPlace.Hotels.push({
                HotelId: hotel.PackageHotelBookingId && !hotel.PackageHotelBookingId.startsWith('new-')
                  ? hotel.PackageHotelBookingId
                  : generateGuid(),
                HotelName: hotel.HotelName || "Suggested Hotel",
                RoomType: hotel.RoomType || "",
                MealPlan: hotel.MealPlan || "",
                StarRating: parseInt(hotel.StarRating) || 0
              });
            }
          }
        });
      });

      const placesList = Array.from(cityMap.values());
      if (placesList.length > 0) {
        const primaryCountry = formData.DestinationsCover?.split(',')[0]?.trim() || "Destination";
        compiledDestinations.push({
          CountryCode: "IN",
          CountryName: primaryCountry,
          Places: placesList
        });
      }

      const updatePayload = {
        Title: formData.Title,
        Description: formData.Description,
        TravelType: formData.TravelType,
        HolidayType: 'Holiday',
        HolidayKey: formData.HolidayKey,
        Duration: formData.Duration,
        TotalNights: formData.TotalNights,
        TotalDays: formData.TotalDays,
        DestinationsCover: formData.DestinationsCover,
        StartedDate: formData.StartedDate ? new Date(formData.StartedDate).toISOString() : null,
        Validity: formData.ValidityFrom && formData.ValidityTo ? {
          From: new Date(formData.ValidityFrom).toISOString(),
          To: new Date(formData.ValidityTo).toISOString()
        } : null,
        NoOfPersons: formData.NoOfPersons,
        Adults: formData.Adults,
        Kids: formData.Kids,
        Infants: formData.Infants,
        Currency: formData.Currency,
        FlightPrice: formData.FlightPrice,
        HotelPrice: formData.HotelPrice,
        SightSeeingPrice: formData.SightSeeingPrice,
        TransferPrice: formData.TransferPrice,
        OtherPrice: formData.OtherPrice,
        PurchasePrice: formData.PurchasePrice,
        SalePrice: formData.SalePrice,
        ContactName: formData.ContactName,
        ContactPhoneNumber: formData.ContactPhoneNumber,
        ContactEmail: formData.ContactEmail,
        Inclusions: formData.Inclusions,
        Exclusions: formData.Exclusions,
        Terms: formData.Terms,
        Destinations: compiledDestinations.length > 0 ? compiledDestinations : null,
        Status: statusMap[formData.Status] !== undefined ? statusMap[formData.Status] : 0,
        ContentType: formData.ContentType,
        IsWebsite: formData.IsWebsite,
        IsLive: formData.IsLive,
        IsPublished: formData.Status === 'published',
        IsFeatured: false,
        ImageName: formData.ImageName || '',
        Media: formData.Media || null,
        OccupancyPricing: formData.OccupancyPricing || null,
        PriceSharing: priceSharingItems,
        // DMC-specific fields
        CompanyId: selectedDmc ? selectedDmc.companyId : user?.cid,
        Source: selectedDmc ? "Dmc" : "Manual",
        CreatedBy: selectedDmc 
          ? (selectedDmc.brandName || selectedDmc.companyName || '') 
          : (user?.name || user?.email || ''),
        CompanyName: selectedDmc 
          ? (selectedDmc.companyName || '') 
          : (user?.companyname || ''),
      };

      if (formData.Itinerary?.length > 0) {
        updatePayload.Itinerary = formData.Itinerary.map(day => ({
          Day: day.Day || 0,
          Title: day.Title || '',
          Description: day.Description || '',
          Location: day.Location || '',
          Meals: day.Meals || '',
          Note: day.Note || '',
          Date: day.Date || '',
          Flights: day.Flights?.map(f => ({
            PackageFlightBookingId: f.PackageFlightBookingId && !f.PackageFlightBookingId.startsWith('new-')
              ? f.PackageFlightBookingId
              : generateGuid(),
            Airline: f.Airline || '',
            FlightNumber: f.FlightNumber || '',
            DepartureCity: f.DepartureCity || '',
            ArrivalCity: f.ArrivalCity || '',
            DepartureDateTime: f.DepartureDateTime || '',
            ArrivalDateTime: f.ArrivalDateTime || '',
            CabinClass: f.CabinClass || '',
            JourneyType: f.JourneyType || '',
            Adults: f.Adults || 0,
            Kids: f.Kids || 0,
            Infants: f.Infants || 0,
            Baggage: f.Baggage || '',
            OptionId: f.OptionId || '',
            IsIncluded: f.IsIncluded !== undefined ? f.IsIncluded : true,
            Price: f.Price || 0,
            Segments: f.Segments?.map(s => ({
              From: s.From || '',
              To: s.To || '',
              Airline: s.Airline || '',
              FlightNo: s.FlightNo || '',
              DepartureTime: s.DepartureTime || '',
              ArrivalTime: s.ArrivalTime || ''
            })) || []
          })) || [],
          Hotels: day.Hotels?.map(h => ({
            PackageHotelBookingId: h.PackageHotelBookingId && !h.PackageHotelBookingId.startsWith('new-')
              ? h.PackageHotelBookingId
              : generateGuid(),
            HotelName: h.HotelName || '',
            City: h.City || '',
            StarRating: h.StarRating || 0,
            RoomType: h.RoomType || '',
            MealPlan: h.MealPlan || '',
            NumberOfRooms: h.NumberOfRooms || 1,
            Nights: h.Nights || 0,
            CheckInDate: h.CheckInDate || '',
            CheckOutDate: h.CheckOutDate || '',
            IsIncluded: h.IsIncluded !== undefined ? h.IsIncluded : true,
            Price: h.Price || 0
          })) || [],
          Sightseeing: day.Sightseeing?.map(s => ({
            PackageSightSeeingId: s.PackageSightSeeingId && !s.PackageSightSeeingId.startsWith('new-')
              ? s.PackageSightSeeingId
              : generateGuid(),
            SightseeingName: s.SightseeingName || '',
            Destination: s.Destination || '',
            Description: s.Description || '',
            Duration: s.Duration || '',
            Type: s.Type || '',
            Date: s.Date || '',
            IsIncluded: s.IsIncluded !== undefined ? s.IsIncluded : true,
            Price: s.Price || 0
          })) || [],
          Transfers: day.Transfers?.map(t => ({
            PackageTransferId: t.PackageTransferId && !t.PackageTransferId.startsWith('new-')
              ? t.PackageTransferId
              : generateGuid(),
            FromLocation: t.FromLocation || '',
            ToLocation: t.ToLocation || '',
            VehicleDetails: t.VehicleDetails || '',
            Type: t.Type || '',
            PickupTime: t.PickupTime || '',
            Date: t.Date || '',
            IsIncluded: t.IsIncluded !== undefined ? t.IsIncluded : true,
            Price: t.Price || 0
          })) || []
        }));
      }

      const formDataToSend = new FormData();

      const appendFormDataHelper = (form, data, parentKey = '') => {
        if (data === null || data === undefined) return;

        if (data instanceof File) {
          form.append(parentKey, data);
        } else if (Array.isArray(data)) {
          data.forEach((item, index) => {
            appendFormDataHelper(form, item, `${parentKey}[${index}]`);
          });
        } else if (typeof data === 'object') {
          Object.keys(data).forEach(key => {
            appendFormDataHelper(form, data[key], parentKey ? `${parentKey}.${key}` : key);
          });
        } else {
          form.append(parentKey, data);
        }
      };

      Object.keys(updatePayload).forEach(key => {
        appendFormDataHelper(formDataToSend, updatePayload[key], key);
      });

      if (selectedFile) {
        formDataToSend.append('ImageFile', selectedFile);
      }

      const response = await apiService.put(
        `${config.UPDATECOMPANYPACKAGE}/${id}`,
        formDataToSend,
        {}
      );

      if (response?.success || response?.succeeded || response?.message === "Package updated successfully.") {
        setToast({ message: 'Package updated successfully! 🎉', type: 'success' });
        setTimeout(() => navigate('/packageslist'), 2000);
      } else {
        throw new Error(response?.message || 'Failed to update package');
      }
    } catch (error) {
      console.error('Error updating package:', error);
      setToast({ message: error.message || 'Failed to update package. Please try again.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  // Step navigation
  const goToStep = (stepId) => setCurrentStep(stepId);
  const goNext = () => setCurrentStep(s => Math.min(6, s + 1));
  const goBack = () => setCurrentStep(s => Math.max(1, s - 1));

  const isStepComplete = (stepId) => {
    switch (stepId) {
      case 1: return Boolean(formData.Title.trim() && formData.Description.trim());
      case 2: return Boolean(formData.DestinationsCover.trim());
      case 3: return Boolean(formData.SalePrice > 0);
      case 4: return Boolean(formData.Status);
      case 5: return formData.Itinerary.length > 0;
      case 6: return formData.Inclusions.length > 0 || formData.Exclusions.length > 0 || formData.Terms.length > 0;
      default: return false;
    }
  };

  const margin = (formData.SalePrice || 0) - (formData.PurchasePrice || 0);

  // Step 1 — Basic Info
  const renderStep1 = () => (
    <div>
      <h2 className="pkg-step-title">Basic Info</h2>
      <p className="pkg-step-subtitle">Update your package title, description, and cover image traveler preview.</p>

      <div className="pkg-dropzone-row">
        <div>
          {/* DMC Selection - New Section */}
          <div className="pkg-form-group">
            <label className="pkg-label">Select DMC (Optional)</label>
            <select 
              className="pkg-select" 
              value={selectedDmc?.companyId || ''} 
              onChange={(e) => {
                const dmc = dmcList.find(d => d.companyId === e.target.value);
                handleDmcSelect(dmc || null);
              }}
            >
             
               <option value="">-- Select DMC --</option>
              {dmcList.map(dmc => (
                <option key={dmc.companyId} value={dmc.companyId}>
                  {dmc.companyName} {dmc.brandName ? `(${dmc.brandName})` : ''}
                </option>
              ))}
            </select>
            {loadingDmcList && <span className="pkg-hint">Loading DMCs...</span>}
            <span className="pkg-hint">Select a DMC to create this package on their behalf, or leave empty to create for yourself.</span>
            {selectedDmc && (
              <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--pkg-success)' }}>
                ✓ Currently associated with: {selectedDmc.companyName} {selectedDmc.brandName ? `(${selectedDmc.brandName})` : ''}
              </div>
            )}
          </div>

          <div className="pkg-form-group">
            <label className="pkg-label">Package Title <span className="pkg-required">*</span></label>
            <input
              className="pkg-input"
              name="Title"
              value={formData.Title}
              onChange={handleInputChange}
              placeholder="e.g., Magical Bali Getaway"
              required
            />
          </div>
          <div className="pkg-form-group">
            <label className="pkg-label">Description <span className="pkg-required">*</span></label>
            <textarea
              className="pkg-textarea"
              name="Description"
              value={formData.Description}
              onChange={handleInputChange}
              rows={4}
              placeholder="Describe what makes this package special..."
              required
            />
          </div>
        </div>

        <div className="pkg-dropzone">
          <div className="pkg-dropzone-preview">
            <img src={imagePreview || DEFAULT_IMAGE} alt="Package cover" />
            {imagePreview && imagePreview !== DEFAULT_IMAGE && (
              <button
                className="pkg-dropzone-remove"
                onClick={() => { setSelectedFile(null); setImagePreview(null); setFormData(prev => ({ ...prev, ImageUrl: '', ImageName: '' })); }}
                type="button"
                title="Remove image"
              >×</button>
            )}
          </div>
          <button className="pkg-upload-btn" onClick={() => fileInputRef.current?.click()} type="button">
            <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleFileChange} />
            {imagePreview && imagePreview !== DEFAULT_IMAGE ? 'Change Cover Image' : 'Upload Cover Image'}
          </button>
          <span className="pkg-upload-hint">JPG or PNG recommended</span>
        </div>
      </div>
    </div>
  );

  // Step 2 — Destinations & Travelers
  const renderStep2 = () => (
    <div>
      <h2 className="pkg-step-title">Destinations &amp; Travelers</h2>
      <p className="pkg-step-subtitle">Manage cities, destinations covered, general travel type, and participant count details.</p>

      <div className="pkg-form-group">
        <label className="pkg-label">Destinations Covered (Places) <span className="pkg-required">*</span></label>
        <div className="pkg-destination-search">
          <input
            className="pkg-input"
            value={destinationInput}
            onChange={handleDestinationInputChange}
            onFocus={() => destinationInput.trim().length > 0 && setShowSuggestions(true)}
            placeholder="Search for destinations to add places..."
          />
          {showSuggestions && destinationSuggestions.length > 0 && (
            <div className="pkg-suggestions">
              {destinationSuggestions.map((dest, idx) => (
                <div
                  key={idx}
                  className="pkg-suggestion-item"
                  onClick={() => handleSelectDestination(dest)}
                >
                  <span>{dest.Destination}</span>
                  <span className="pkg-suggestion-place">
                    {dest.Places.slice(0, 3).join(', ')}{dest.Places.length > 3 ? '...' : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pkg-manual-add">
          <input
            className="pkg-input"
            id="manualPlaceInput"
            placeholder="Add place manually (e.g., Delhi, Mumbai)..."
            onKeyPress={(e) => { if (e.key === 'Enter') { handleManualPlaceAdd(); } }}
          />
          <button className="pkg-btn pkg-btn-secondary" type="button" onClick={handleManualPlaceAdd}>
            + Add Place
          </button>
        </div>

        <div className="pkg-pills">
          {selectedPlaces.map((p, idx) => (
            <span key={idx} className="pkg-pill">
              📍 {p.place}
              {p.destination !== 'Manual Entry' && ` (${p.destination})`}
              <span className="pkg-pill-remove" onClick={() => handleRemoveSelectedPlace(p)}>×</span>
            </span>
          ))}
          {selectedPlaces.length === 0 && (
            <span className="pkg-empty-hint">No places added yet. Search or enter manually.</span>
          )}
        </div>
        <input type="hidden" name="DestinationsCover" value={formData.DestinationsCover} readOnly />
      </div>

      {showPlaceDropdown && currentDestination && (
        <div className="pkg-place-panel">
          <div className="pkg-place-panel-header">
            <label className="pkg-label" style={{ margin: 0 }}>Select Places for {currentDestination.Destination}</label>
            <button className="pkg-btn-remove" onClick={() => setShowPlaceDropdown(false)} type="button">Close</button>
          </div>
          <div className="pkg-place-chips">
            {currentDestination.Places.map((place, idx) => (
              <span
                key={idx}
                className={`pkg-place-chip ${isPlaceSelected(place) ? 'selected' : ''}`}
                onClick={() => handleTogglePlace(place)}
              >
                {place}{isPlaceSelected(place) && ' ✓'}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="pkg-grid-auto" style={{ marginTop: '20px' }}>
        <div className="pkg-form-group">
          <label className="pkg-label">Travel Type</label>
          <select className="pkg-select" name="TravelType" value={formData.TravelType} onChange={handleInputChange}>
            <option value="">Select</option>
            {travelTypes.map(type => <option key={type} value={type}>{type}</option>)}
          </select>
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Total Nights</label>
          <input
            type="number"
            className="pkg-input"
            name="TotalNights"
            value={formData.TotalNights}
            onChange={(e) => handleDaysNightsChange('TotalNights', e.target.value)}
            min="0"
            placeholder="Nights"
          />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Adults</label>
          <input type="number" className="pkg-input" name="Adults" value={formData.Adults} onChange={handleInputChange} min="0" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Kids</label>
          <input type="number" className="pkg-input" name="Kids" value={formData.Kids} onChange={handleInputChange} min="0" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Infants</label>
          <input type="number" className="pkg-input" name="Infants" value={formData.Infants} onChange={handleInputChange} min="0" />
        </div>
        <div className="pkg-form-group">
          <label className="pkg-label">Total Persons</label>
          <input type="number" className="pkg-input" name="NoOfPersons" value={formData.NoOfPersons} disabled readOnly />
        </div>
      </div>
    </div>
  );

  // Step 3 — Dates & Pricing
  const renderStep3 = () => (
    <div>
      <h2 className="pkg-step-title">Dates &amp; Pricing</h2>
      <p className="pkg-step-subtitle">Configure dates, validity limits, base currency, sales prices, and nested share-pricing enums.</p>

      <div className="pkg-section">
        <div className="pkg-section-heading">📅 Dates</div>
        <div className="pkg-grid-3">
          <div className="pkg-form-group">
            <label className="pkg-label">Start Date</label>
            <input type="date" className="pkg-input" name="StartedDate" value={formData.StartedDate} onChange={handleInputChange} />
          </div>
          <div className="pkg-form-group">
            <label className="pkg-label">Validity From</label>
            <input type="date" className="pkg-input" name="ValidityFrom" value={formData.ValidityFrom} onChange={handleInputChange} />
          </div>
          <div className="pkg-form-group">
            <label className="pkg-label">Validity To</label>
            <input type="date" className="pkg-input" name="ValidityTo" value={formData.ValidityTo} onChange={handleInputChange} />
          </div>
        </div>
      </div>

      <div className="pkg-section">
        <div className="pkg-section-heading">💰 Pricing</div>
        <div className="pkg-grid-3">
          <div className="pkg-form-group">
            <label className="pkg-label">Currency</label>
            <select className="pkg-select" name="Currency" value={formData.Currency} onChange={handleInputChange}>
              <option value="">Select Currency</option>
              {currencyOptions.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.code} - {currency.name}
                </option>
              ))}
            </select>
          </div>
          <div className="pkg-form-group">
            <label className="pkg-label">No of Persons</label>
            <input
              type="number"
              className="pkg-input"
              name="NoOfPersons"
              value={formData.NoOfPersons}
              onChange={handleInputChange}
              min="1"
              placeholder="Total persons"
            />
          </div>
          <div className="pkg-form-group">
            <label className="pkg-label">Sale Price <span className="pkg-required">*</span></label>
            <input
              type="number"
              className="pkg-input"
              name="SalePrice"
              value={formData.SalePrice}
              onChange={handleInputChange}
              min="0"
              step="0.01"
              placeholder="0.00"
              required
            />
          </div>
        </div>

        <div className="pkg-margin-strip">
          <div className="pkg-margin-item">
            <span>Purchase Price</span>
            <span className="pkg-margin-value">{formData.Currency} {(formData.PurchasePrice || 0).toFixed(2)}</span>
          </div>
          <div className="pkg-margin-item">
            <span>Sale Price</span>
            <span className="pkg-margin-value">{formData.Currency} {(formData.SalePrice || 0).toFixed(2)}</span>
          </div>
          <div className="pkg-margin-item">
            <span>Profit Margin</span>
            <span className={`pkg-margin-value ${margin > 0 ? 'positive' : margin < 0 ? 'negative' : ''}`}>
              {formData.Currency} {margin.toFixed(2)}
            </span>
          </div>
        </div>

        <div className="pkg-section">
          <div className="pkg-section-heading" style={{ display: 'flex', justifyContent: 'space-between', border: 'none', paddingBottom: 0 }}>
            <span>👥 Price Sharing</span>
            <button className="pkg-btn pkg-btn-xs pkg-btn-secondary" type="button" onClick={handleAddPriceSharing}>
              + Add Price Tier
            </button>
          </div>

          {priceSharingItems.length === 0 ? (
            <p className="pkg-empty-hint" style={{ padding: '8px 0' }}>
              No price sharing tiers added. Click "Add Price Tier" to configure.
            </p>
          ) : (
            priceSharingItems.map((item, index) => (
              <div key={index} className="pkg-subcard-tier">
                <div className="pkg-subcard-header">
                  <strong>Price Tier {index + 1}</strong>
                  <button className="pkg-btn-remove" onClick={() => handleRemovePriceSharing(index)} type="button">Remove</button>
                </div>
                <div className="pkg-grid-4">
                  <div className="pkg-form-group">
                    <label className="pkg-label">Price</label>
                    <input type="number" className="pkg-input" value={item.Price || 0} onChange={(e) => handleUpdatePriceSharing(index, 'Price', parseFloat(e.target.value) || 0)} min="0" step="0.01" placeholder="0.00" />
                  </div>
                  <div className="pkg-form-group">
                    <label className="pkg-label">Sharing Type</label>
                    <select className="pkg-select" value={item.SharingType || 'Double'} onChange={(e) => handleUpdatePriceSharing(index, 'SharingType', e.target.value)}>
                      <option value="Double">Double</option>
                      <option value="Single">Single</option>
                      <option value="Triple">Triple</option>
                      <option value="Quad">Quad</option>
                      <option value="Child with Bed">Child with Bed</option>
                      <option value="Child without Bed">Child without Bed</option>
                      <option value="Infant">Infant</option>
                    </select>
                  </div>
                  <div className="pkg-form-group">
                    <label className="pkg-label">Adult Price</label>
                    <input type="number" className="pkg-input" value={item.AdultPrice || 0} onChange={(e) => handleUpdatePriceSharing(index, 'AdultPrice', parseFloat(e.target.value) || 0)} min="0" step="0.01" placeholder="0.00" />
                  </div>
                  <div className="pkg-form-group">
                    <label className="pkg-label">Kid Price</label>
                    <input type="number" className="pkg-input" value={item.KidPrice || 0} onChange={(e) => handleUpdatePriceSharing(index, 'KidPrice', parseFloat(e.target.value) || 0)} min="0" step="0.01" placeholder="0.00" />
                  </div>
                  <div className="pkg-form-group">
                    <label className="pkg-label">Infant Price</label>
                    <input type="number" className="pkg-input" value={item.InfantPrice || 0} onChange={(e) => handleUpdatePriceSharing(index, 'InfantPrice', parseFloat(e.target.value) || 0)} min="0" step="0.01" placeholder="0.00" />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="pkg-section" style={{ marginBottom: 0 }}>
          <div className="pkg-estimation-toggle" onClick={() => setShowEstimation(!showEstimation)}>
            <span>💰 Cost Breakdown (Flight / Hotel / Sightseeing / Transfer / Other)</span>
            <span>{showEstimation ? '▼' : '▶'}</span>
          </div>
          {showEstimation && (
            <div className="pkg-estimation-body">
              <div className="pkg-grid-3">
                <div className="pkg-form-group">
                  <label className="pkg-label">Flight</label>
                  <input type="number" className="pkg-input" name="FlightPrice" value={formData.FlightPrice} onChange={handleInputChange} min="0" step="0.01" placeholder="0.00" />
                </div>
                <div className="pkg-form-group">
                  <label className="pkg-label">Hotel</label>
                  <input type="number" className="pkg-input" name="HotelPrice" value={formData.HotelPrice} onChange={handleInputChange} min="0" step="0.01" placeholder="0.00" />
                </div>
                <div className="pkg-form-group">
                  <label className="pkg-label">Sightseeing</label>
                  <input type="number" className="pkg-input" name="SightSeeingPrice" value={formData.SightSeeingPrice} onChange={handleInputChange} min="0" step="0.01" placeholder="0.00" />
                </div>
              </div>
              <div className="pkg-grid-3">
                <div className="pkg-form-group">
                  <label className="pkg-label">Transfer</label>
                  <input type="number" className="pkg-input" name="TransferPrice" value={formData.TransferPrice} onChange={handleInputChange} min="0" step="0.01" placeholder="0.00" />
                </div>
                <div className="pkg-form-group">
                  <label className="pkg-label">Other</label>
                  <input type="number" className="pkg-input" name="OtherPrice" value={formData.OtherPrice} onChange={handleInputChange} min="0" step="0.01" placeholder="0.00" />
                </div>
                <div className="pkg-form-group">
                  <label className="pkg-label">Purchase Price</label>
                  <input type="number" className="pkg-input" name="PurchasePrice" value={formData.PurchasePrice} onChange={handleInputChange} min="0" step="0.01" placeholder="0.00" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Step 4 — Settings & Contact
  const renderStep4 = () => (
    <div>
      <h2 className="pkg-step-title">Settings &amp; Contact</h2>
      <p className="pkg-step-subtitle">Maintain visibility settings, public state toggles, and organizer details.</p>

      <div className="pkg-section">
        <div className="pkg-grid-3">
          <div className="pkg-form-group">
            <label className="pkg-label">Status</label>
            <select className="pkg-select" name="Status" value={formData.Status} onChange={handleInputChange}>
              {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="pkg-form-group">
            <label className="pkg-label">Is Live</label>
            <div className="pkg-toggle-row">
              <label className="pkg-switch">
                <input type="checkbox" name="IsLive" checked={formData.IsLive} onChange={handleInputChange} />
                <span className="pkg-switch-slider"></span>
              </label>
              <span className="pkg-toggle-label">Make this package live</span>
            </div>
          </div>
          <div className="pkg-form-group">
            <label className="pkg-label">Show on Website</label>
            <div className="pkg-toggle-row">
              <label className="pkg-switch">
                <input type="checkbox" name="IsWebsite" checked={formData.IsWebsite} onChange={handleInputChange} />
                <span className="pkg-switch-slider"></span>
              </label>
              <span className="pkg-toggle-label">Display on public website</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pkg-contact-panel">
        <div className="pkg-section-heading" style={{ border: 'none', paddingBottom: 0, color: 'var(--pkg-primary-dark)' }}>
          📞 Contact Information
          {selectedDmc && (
            <span style={{ fontSize: '12px', fontWeight: 'normal', color: 'var(--pkg-text-secondary)', marginLeft: '8px' }}>
              (Auto-filled from DMC: {selectedDmc.companyName})
            </span>
          )}
        </div>
        <div className="pkg-grid-3">
          <div className="pkg-form-group">
            <label className="pkg-label">Contact Name</label>
            <input 
              className="pkg-input" 
              name="ContactName" 
              value={formData.ContactName || ''} 
              onChange={handleInputChange} 
              placeholder="Name"
              readOnly={!!selectedDmc}
              style={selectedDmc ? { backgroundColor: 'var(--pkg-bg-soft)' } : {}}
            />
          </div>
          <div className="pkg-form-group">
            <label className="pkg-label">Email</label>
            <input 
              type="email" 
              className="pkg-input" 
              name="ContactEmail" 
              value={formData.ContactEmail || ''} 
              onChange={handleInputChange} 
              placeholder="Email"
              readOnly={!!selectedDmc}
              style={selectedDmc ? { backgroundColor: 'var(--pkg-bg-soft)' } : {}}
            />
          </div>
          <div className="pkg-form-group">
            <label className="pkg-label">Phone Number</label>
            <input 
              type="tel" 
              className="pkg-input" 
              name="ContactPhoneNumber" 
              value={formData.ContactPhoneNumber || ''} 
              onChange={handleInputChange} 
              placeholder="Phone"
              readOnly={!!selectedDmc}
              style={selectedDmc ? { backgroundColor: 'var(--pkg-bg-soft)' } : {}}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Step 5 — Itinerary
  const renderStep5 = () => (
    <div>
      <h2 className="pkg-step-title">Itinerary</h2>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
        <p className="pkg-step-subtitle" style={{ margin: 0 }}>Review daily segments of flights, hotels, excursions, and transport bookings.</p>
        <button className="pkg-btn pkg-btn-primary" type="button" onClick={handleAddItineraryDay}>+ Add Day</button>
      </div>

      {formData.Itinerary.length === 0 ? (
        <div className="pkg-empty-state">
          <span className="pkg-empty-icon">🗓️</span>
          <p>No itinerary days added yet. Click "Add Day" to start.</p>
        </div>
      ) : (
        formData.Itinerary.map((day, index) => (
          <div key={index} className="pkg-itinerary-day">
            <div className="pkg-itinerary-day-header">
              <h3>📅 Day {day.Day || index + 1}</h3>
              <div className="pkg-day-actions">
                <button className="pkg-btn pkg-btn-xs pkg-btn-secondary" onClick={() => handleAddFlight(index)} type="button">✈️ Add Flight</button>
                <button className="pkg-btn pkg-btn-xs pkg-btn-secondary" onClick={() => handleAddHotel(index)} type="button">🏨 Add Hotel</button>
                <button className="pkg-btn pkg-btn-xs pkg-btn-secondary" onClick={() => handleAddSightseeing(index)} type="button">🗺️ Add Sightseeing</button>
                <button className="pkg-btn pkg-btn-xs pkg-btn-secondary" onClick={() => handleAddTransfer(index)} type="button">🚗 Add Transfer</button>
                <button className="pkg-btn-remove" onClick={() => handleRemoveItineraryDay(index)} type="button">Remove Day</button>
              </div>
            </div>

            <div className="pkg-grid-2">
              <div className="pkg-form-group">
                <label className="pkg-label">Title</label>
                <input className="pkg-input" value={day.Title || ''} onChange={(e) => handleItineraryChange(index, 'Title', e.target.value)} placeholder="Day title" />
              </div>
              <div className="pkg-form-group">
                <label className="pkg-label">Location</label>
                <input className="pkg-input" value={day.Location || ''} onChange={(e) => handleItineraryChange(index, 'Location', e.target.value)} placeholder="Location" />
              </div>
            </div>
            <div className="pkg-form-group">
              <label className="pkg-label">Description</label>
              <textarea className="pkg-textarea" style={{ minHeight: '48px' }} rows={2} value={day.Description || ''} onChange={(e) => handleItineraryChange(index, 'Description', e.target.value)} placeholder="Description" />
            </div>
            <div className="pkg-grid-3">
              <div className="pkg-form-group">
                <label className="pkg-label">Meals</label>
                <input className="pkg-input" value={day.Meals || ''} onChange={(e) => handleItineraryChange(index, 'Meals', e.target.value)} placeholder="e.g., Breakfast" />
              </div>
              <div className="pkg-form-group">
                <label className="pkg-label">Date</label>
                <input type="date" className="pkg-input" value={day.Date || ''} onChange={(e) => handleItineraryChange(index, 'Date', e.target.value)} />
              </div>
              <div className="pkg-form-group">
                <label className="pkg-label">Note</label>
                <input className="pkg-input" value={day.Note || ''} onChange={(e) => handleItineraryChange(index, 'Note', e.target.value)} placeholder="Special note" />
              </div>
            </div>

            {(day.Flights || []).length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <div className="pkg-section-heading">✈️ Flights</div>
                {day.Flights.map((flight, fIdx) => (
                  <FlightSection
                    key={fIdx}
                    flight={flight}
                    index={fIdx}
                    onUpdate={(flightIndex, updatedFlight) => handleUpdateFlight(index, flightIndex, updatedFlight)}
                    onRemove={(flightIndex) => handleRemoveFlight(index, flightIndex)}
                  />
                ))}
              </div>
            )}

            {(day.Hotels || []).length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <div className="pkg-section-heading">🏨 Hotels</div>
                {day.Hotels.map((hotel, hIdx) => (
                  <HotelSection
                    key={hIdx}
                    hotel={hotel}
                    index={hIdx}
                    onUpdate={(hotelIndex, updatedHotel) => handleUpdateHotel(index, hotelIndex, updatedHotel)}
                    onRemove={(hotelIndex) => handleRemoveHotel(index, hotelIndex)}
                  />
                ))}
              </div>
            )}

            {(day.Sightseeing || []).length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <div className="pkg-section-heading">🗺️ Sightseeing</div>
                {day.Sightseeing.map((ss, sIdx) => (
                  <SightseeingSection
                    key={sIdx}
                    sightseeing={ss}
                    index={sIdx}
                    onUpdate={(ssIndex, updatedSS) => handleUpdateSightseeing(index, ssIndex, updatedSS)}
                    onRemove={(ssIndex) => handleRemoveSightseeing(index, ssIndex)}
                  />
                ))}
              </div>
            )}

            {(day.Transfers || []).length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <div className="pkg-section-heading">🚗 Transfers</div>
                {day.Transfers.map((transfer, tIdx) => (
                  <TransferSection
                    key={tIdx}
                    transfer={transfer}
                    index={tIdx}
                    onUpdate={(transferIndex, updatedTransfer) => handleUpdateTransfer(index, transferIndex, updatedTransfer)}
                    onRemove={(transferIndex) => handleRemoveTransfer(index, transferIndex)}
                  />
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  // Step 6 — Inclusions, Exclusions & Terms
  const renderStep6 = () => (
    <div>
      <h2 className="pkg-step-title">Inclusions, Exclusions &amp; Terms</h2>
      <p className="pkg-step-subtitle">Ensure policy declarations, terms of booking, and inclusions are detailed properly.</p>

      <div className="pkg-subtab-pills">
        {inclusionSubTabs.map(subTab => (
          <button
            key={subTab.id}
            className={`pkg-subtab-pill ${inclusionActiveSubTab === subTab.id ? 'active' : ''}`}
            onClick={() => setInclusionActiveSubTab(subTab.id)}
            type="button"
          >
            {subTab.label}
          </button>
        ))}
      </div>

      {inclusionActiveSubTab === 'inclusions' && (
        <div>
          <div className="pkg-add-row">
            <input className="pkg-input" id="inclusionInput" placeholder="Add included item..." onKeyPress={(e) => { if (e.key === 'Enter') { handleAddArrayItem('Inclusions', e.target.value); e.target.value = ''; } }} />
            <button className="pkg-btn pkg-btn-secondary" type="button" onClick={() => { const input = document.getElementById('inclusionInput'); handleAddArrayItem('Inclusions', input.value); input.value = ''; }}>Add</button>
          </div>
          <div className="pkg-chip-list">
            {formData.Inclusions.map((item, index) => (
              <span key={index} className="pkg-chip">✅ {item}<span className="pkg-chip-remove" onClick={() => handleRemoveArrayItem('Inclusions', index)}>×</span></span>
            ))}
            {formData.Inclusions.length === 0 && <p className="pkg-empty-hint">No inclusions added yet.</p>}
          </div>
        </div>
      )}

      {inclusionActiveSubTab === 'exclusions' && (
        <div>
          <div className="pkg-add-row">
            <input className="pkg-input" id="exclusionInput" placeholder="Add excluded item..." onKeyPress={(e) => { if (e.key === 'Enter') { handleAddArrayItem('Exclusions', e.target.value); e.target.value = ''; } }} />
            <button className="pkg-btn pkg-btn-secondary" type="button" onClick={() => { const input = document.getElementById('exclusionInput'); handleAddArrayItem('Exclusions', input.value); input.value = ''; }}>Add</button>
          </div>
          <div className="pkg-chip-list">
            {formData.Exclusions.map((item, index) => (
              <span key={index} className="pkg-chip">❌ {item}<span className="pkg-chip-remove" onClick={() => handleRemoveArrayItem('Exclusions', index)}>×</span></span>
            ))}
            {formData.Exclusions.length === 0 && <p className="pkg-empty-hint">No exclusions added yet.</p>}
          </div>
        </div>
      )}

      {inclusionActiveSubTab === 'terms' && (
        <div>
          <div className="pkg-grid-2">
            <div className="pkg-form-group">
              <label className="pkg-label">Term Title</label>
              <input className="pkg-input" id="termTitle" placeholder="e.g., Cancellation Policy" />
            </div>
            <div className="pkg-form-group">
              <label className="pkg-label">Description</label>
              <input className="pkg-input" id="termDescription" placeholder="Description" />
            </div>
          </div>
          <button className="pkg-btn pkg-btn-secondary" style={{ marginBottom: '14px' }} type="button" onClick={handleAddTerm}>+ Add Term</button>
          <div className="pkg-terms-list">
            {formData.Terms.map((term, index) => (
              <div key={index} className="pkg-term-card">
                <div>
                  <strong>{term.Title}</strong>
                  {term.Description && <p>{term.Description}</p>}
                </div>
                <span className="pkg-chip-remove" onClick={() => handleRemoveTerm(index)}>×</span>
              </div>
            ))}
            {formData.Terms.length === 0 && <p className="pkg-empty-hint">No terms added yet.</p>}
          </div>
        </div>
      )}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      default: return null;
    }
  };

  if (authLoading || isFetching) {
    return (
      <div className="pkg-loading-overlay">
        <div className="pkg-spinner"></div>
      </div>
    );
  }

  if (!user) {
    navigate('/');
    return null;
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="pkg-create">
            {toast && (
              <Toast
                message={toast.message}
                type={toast.type}
                onClose={() => setToast(null)}
              />
            )}

            {isLoading && (
              <div className="pkg-loading-overlay">
                <div className="pkg-spinner"></div>
              </div>
            )}

            <>
              <div className="pkg-topbar">
                <div>
                  <h1>✏️ Edit Package</h1>
                  <p>Update package details including flights, hotels, sightseeing, and transfers</p>
                </div>
                <div className="pkg-topbar-actions">
                  <button className="pkg-btn pkg-btn-secondary" onClick={() => navigate('/packageslist')} type="button">← Back</button>
   
    {/* Added the Update Package button to the top actions bar */}
                  <button
                    className="pkg-btn pkg-btn-primary"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    type="button"
                  >
                    {isLoading ? 'Updating...' : '💾 Update Package'}
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="pkg-stepper">
                  {STEPS.map((step, idx) => (
                    <React.Fragment key={step.id}>
                      <button
                        type="button"
                        className={`pkg-step ${currentStep === step.id ? 'pkg-step-active' : ''} ${isStepComplete(step.id) ? 'pkg-step-complete' : ''}`}
                        onClick={() => goToStep(step.id)}
                      >
                        <span className="pkg-step-circle">
                          {isStepComplete(step.id) && currentStep !== step.id ? '✓' : step.id}
                        </span>
                        <span className="pkg-step-label">{step.label}</span>
                      </button>
                      {idx < STEPS.length - 1 && <span className="pkg-step-line" />}
                    </React.Fragment>
                  ))}
                </div>

                <div className="pkg-card">
                  <div className="pkg-step-panel">
                    {renderStepContent()}
                  </div>
                </div>

                <div className="pkg-footer-nav">
                  <div className="pkg-footer-left">
                    <button type="button" className="pkg-btn pkg-btn-secondary" onClick={() => navigate('/packageslist')}>Cancel</button>
                    <button
                      type="button"
                      className="pkg-btn pkg-btn-secondary"
                      onClick={() => { if (window.confirm('Are you sure you want to revert all changes?')) { window.location.reload(); } }}
                    >
                      Reset
                    </button>
                  </div>
                  <div className="pkg-footer-right">
                    {currentStep > 1 && (
                      <button type="button" className="pkg-btn pkg-btn-secondary" onClick={goBack}>← Back</button>
                    )}
                    {currentStep < 6 ? (
                      <button type="button" className="pkg-btn pkg-btn-primary" onClick={goNext}>Next →</button>
                    ) : null}
                  </div>
                </div>
              </form>
            </>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyPackageEdit;